import React from "react";
import { ShieldAlert, Flame, MessageCircleHeart, Activity, MessageCircle, Stethoscope, CalendarCheck } from "lucide-react";
import { Reveal, SectionHeading, GlowOrb } from "@/components/landing/primitives";

const QUESTIONS = [
  "Est-ce qu'elle joue avec moi ou elle est vraiment intéressée ?",
  "Ces messages, c'est du love bombing ou de l'amour ?",
  "Pourquoi il met 3h à répondre maintenant ?",
  "Ma relation se dégrade ou je fais une fixette ?",
];

const PILLARS = [
  { icon: ShieldAlert, title: "Détecte les red flags", desc: "Love bombing, ghosting, manipulation… on te dit tout, sans filtre." },
  { icon: Flame, title: "Note ton Rizz Score", desc: "On note ton game sur 100. Prépare ton ego." },
  { icon: MessageCircleHeart, title: "Suggère 3 réponses", desc: "Safe, bold ou drôle. Copie/colle et réponds comme un boss." },
  { icon: Activity, title: "Suit tes relations 24/7", desc: "Health score, alertes de dégradation, tendances. Comme un check-up médical." },
];

export function ProblemSection() {
  return (
    <section className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <Reveal>
          <SectionHeading eyebrow="On te comprend" title="Tu te poses ces questions ?" center />
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 gap-4">
          {QUESTIONS.map((q, i) => (
            <Reveal key={q} delay={i * 0.08}>
              <div className="rounded-3xl bg-gradient-to-br from-zinc-900 to-black border border-white/10 p-6 hover:border-pink-500/30 transition-colors">
                <div className="inline-block rounded-2xl rounded-bl-sm bg-zinc-800/80 px-4 py-3">
                  <p className="text-sm text-zinc-200 leading-snug">{q}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 text-center text-lg font-display font-bold text-white">
            DocLove te donne la vérité. <span className="text-pink-400">Sans complaisance, sans jugement.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function SolutionSection() {
  return (
    <section className="relative py-20 md:py-28 bg-gradient-to-b from-transparent via-pink-950/10 to-transparent">
      <GlowOrb className="w-[400px] h-[400px] top-10 left-1/2 -translate-x-1/2" color="#8b5cf6" opacity={0.1} />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 relative">
        <Reveal>
          <SectionHeading eyebrow="La méthode" title="Ce que DocLove fait pour toi" center />
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map(({ icon: Icon, title, desc }, i) => (
            <Reveal key={title} delay={i * 0.08}>
              <div className="h-full rounded-3xl bg-[#0a0a0a] border border-white/10 p-6 hover:border-pink-500/30 hover:shadow-[0_8px_32px_rgba(236,72,153,0.1)] transition-colors">
                <div className="w-12 h-12 rounded-2xl bg-pink-500/10 border border-pink-500/20 flex items-center justify-center text-pink-400 shadow-[0_0_18px_rgba(236,72,153,0.25)]">
                  <Icon size={22} aria-label={title} />
                </div>
                <h3 className="mt-5 font-bold text-white">{title}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { n: "1", title: "Colle ta conv", desc: "WhatsApp, Tinder, Hinge, Insta. 100% anonyme." },
  { n: "2", title: "DocLove analyse", desc: "GPT-4o entraîné sur la culture dating FR, en 10 sec." },
  { n: "3", title: "Reçois ton rapport", desc: "Red flags, rizz score et 3 réponses prêtes à envoyer." },
];

export function HowItWorks() {
  return (
    <section className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <Reveal>
          <SectionHeading eyebrow="Simple" title="3 étapes, 30 secondes" center />
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-8 md:gap-6 relative">
          <div className="hidden md:block absolute top-6 left-[16%] right-[16%] h-px bg-gradient-to-r from-pink-500/60 via-purple-500/40 to-transparent" />
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12} className="relative">
              <div className="flex md:flex-col items-start md:items-center gap-4 md:text-center">
                <div className="relative shrink-0 w-12 h-12 rounded-full bg-pink-500 text-white font-display font-black flex items-center justify-center shadow-[0_0_22px_rgba(236,72,153,0.5)]">
                  {s.n}
                  <span className="absolute inset-0 rounded-full bg-pink-500 animate-ping opacity-20" />
                </div>
                <div>
                  <h3 className="font-bold text-white">{s.title}</h3>
                  <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed max-w-xs">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const RF_EXAMPLES = [
  {
    tag: "Love bombing",
    sev: "Élevé",
    message: "Je crois que t'es la femme de ma vie 🥹 On se connaît depuis 3 jours mais je ressens un truc énorme, je peux plus me passer de toi.",
    analysis: "Intensité émotionnelle disproportionnée beaucoup trop tôt. Classique du love bombing : vérifie si les actes suivent les mots sur la durée avant de t'emballer.",
  },
  {
    tag: "Breadcrumbing",
    sev: "Moyen",
    message: "Coucou toi 🙈 (après 2 semaines de silence… et toujours aucune proposition de se voir)",
    analysis: "Il/elle relance juste assez pour te garder sous le coude, sans réelle intention. Investissement à sens unique : ne réorganise pas ta vie pour ces miettes.",
  },
  {
    tag: "Gaslighting",
    sev: "Élevé",
    message: "J'ai jamais dit ça, tu te fais des films. T'es parano en fait, c'est toi le problème.",
    analysis: "Remise en cause de ta perception de la réalité + inversion de la faute. Signal toxique : garde des traces et fais confiance à ton ressenti.",
  },
];

const SEV_COLOR = { "Élevé": "text-red-400 bg-red-500/10 border-red-500/25", "Moyen": "text-amber-400 bg-amber-500/10 border-amber-500/25" };

const FLAGSHIP = [
  {
    icon: MessageCircle,
    badge: "Pro",
    badgeColor: "text-emerald-300 bg-emerald-500/10 border-emerald-500/25",
    title: "Suivi WhatsApp 24/7",
    desc: "Connecte WhatsApp et laisse le Doc veiller sur ta relation en continu. Il analyse tes échanges au fil de l'eau, calcule un health score qui évolue, et t'alerte dès qu'une dégradation ou un red flag apparaît.",
    points: ["Health score en temps réel", "Alertes de dégradation", "Jusqu'à 5 relations suivies", "Notes vocales transcrites"],
    glow: "#10b981",
  },
  {
    icon: Stethoscope,
    badge: "Docteur",
    badgeColor: "text-pink-300 bg-pink-500/10 border-pink-500/25",
    title: "Consultations vocales avec ton coach IA",
    desc: "Parle à ton Docteur Love comme à un vrai coach au téléphone. Raconte ta situation à voix haute, il t'écoute, t'analyse et te répond en vocal — avec tout le contexte de tes relations suivies.",
    points: ["Parle, il te répond en audio", "Conseils personnalisés", "Mémoire de vos échanges", "Dispo 24/7, sans jugement"],
    glow: "#EC4899",
  },
  {
    icon: CalendarCheck,
    badge: "Docteur",
    badgeColor: "text-pink-300 bg-pink-500/10 border-pink-500/25",
    title: "Plans d'action sur-mesure",
    desc: "Le Doc te crée un plan personnalisé sur plusieurs semaines pour améliorer ta relation, avec des actions concrètes semaine par semaine. Et il te relance pour faire le point sur tes progrès.",
    points: ["Plan multi-semaines concret", "Tâches à cocher", "Check-ins de suivi", "Basé sur l'analyse de ta relation"],
    glow: "#a855f7",
  },
];

export function FlagshipFeatures() {
  return (
    <section className="relative py-20 md:py-28 bg-gradient-to-b from-transparent via-pink-950/10 to-transparent">
      <GlowOrb className="w-[420px] h-[420px] top-20 right-0" color="#EC4899" opacity={0.1} />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 relative">
        <Reveal>
          <SectionHeading
            eyebrow="Premium"
            title="Bien plus qu'une simple analyse"
            sub="DocLove ne s'arrête pas au diagnostic. Il veille, il te coache, il t'accompagne dans la durée."
            center
          />
        </Reveal>
        <div className="mt-14 space-y-6">
          {FLAGSHIP.map(({ icon: Icon, badge, badgeColor, title, desc, points, glow }, i) => (
            <Reveal key={title} delay={i * 0.08}>
              <div className="grid md:grid-cols-2 gap-8 items-center rounded-3xl bg-[#0a0a0a] border border-white/10 p-6 md:p-10 hover:border-pink-500/30 transition-colors overflow-hidden">
                <div className={i % 2 ? "md:order-2" : ""}>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest rounded-full px-2.5 py-1 border ${badgeColor}`}>
                    {badge}
                  </span>
                  <h3 className="mt-4 font-display text-2xl md:text-3xl font-black text-white tracking-tight">{title}</h3>
                  <p className="mt-3 text-zinc-400 leading-relaxed">{desc}</p>
                  <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2">
                    {points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-zinc-300">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-pink-400 shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`relative flex justify-center ${i % 2 ? "md:order-1" : ""}`}>
                  <div className="absolute w-48 h-48 rounded-full blur-3xl opacity-30" style={{ background: glow }} aria-hidden />
                  <div className="relative w-28 h-28 md:w-36 md:h-36 rounded-[2rem] bg-gradient-to-br from-zinc-900 to-black border border-white/10 flex items-center justify-center">
                    <Icon size={54} style={{ color: glow }} aria-label={title} />
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}


export function AnalysisShowcase() {
  return (
    <section className="relative py-20 md:py-28 bg-gradient-to-b from-transparent via-purple-950/10 to-transparent">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <Reveal>
          <SectionHeading
            eyebrow="Concret"
            title="Ce que DocLove détecte pour toi"
            sub="Des schémas toxiques pas toujours évidents à repérer. Voici comment le Doc les décode."
            center
          />
        </Reveal>
        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {RF_EXAMPLES.map((e, i) => (
            <Reveal key={e.tag} delay={i * 0.1}>
              <div className="h-full rounded-3xl bg-[#0a0a0a] border border-white/10 p-6 flex flex-col">
                <span className={`self-start inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 border ${SEV_COLOR[e.sev]}`}>
                  🚩 {e.tag} · {e.sev}
                </span>
                <div className="mt-4 rounded-2xl rounded-bl-sm bg-zinc-800/80 px-4 py-3">
                  <p className="text-sm text-zinc-200 leading-snug">{e.message}</p>
                </div>
                <div className="mt-4 flex items-start gap-2 text-sm text-zinc-400 leading-relaxed">
                  <ShieldAlert size={16} className="text-pink-400 shrink-0 mt-0.5" />
                  <span><span className="text-pink-400 font-semibold">Analyse DocLove : </span>{e.analysis}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-6 text-center text-xs text-zinc-600">Exemples anonymisés à titre illustratif.</p>
        </Reveal>
      </div>
    </section>
  );
}
